import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NewTableComponent from '../../../components/NewTableComponent';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { partModelStatusOptions } from '../../../utils/Dropdowns';
import { Badge } from 'react-bootstrap';
import PartModelOverviewCards from './PartModelOverviewCards';
import CreatePartModelModal from './CreatePartModelModal';
import UpdatePartModelModal from './UpdatePartModelModal';
import { plusIcon } from '../../../utils/svgs';
import Button from '../../../components/button';
import { LuPencil } from 'react-icons/lu';
import { RiDeleteBinLine } from 'react-icons/ri';
import CustomModal from '../../../components/CustomModal';
import { deletePartModelAsync, fetchPartModels, getPartModelsOverview } from '../../../store/Slices/PartModelSlice';
import { errorNotify, successNotify } from '../../../utils/toast';

const PartModels = () => {
    const dispatch = useDispatch();
    const { partModelsData, partModelsLoading, deletePartModelLoading } = useSelector((state) => state.partModels);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [searchFilters, setSearchFilters] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPartModel, setSelectedPartModel] = useState(null);
    const [selectedPartModelId, setSelectedPartModelId] = useState(null);

    const handleEditPartModel = (partModel) => {
        setSelectedPartModel(partModel);
        setShowUpdateModal(true);
    };

    const handleDeletePartModel = async () => {
        try {
            await dispatch(deletePartModelAsync(selectedPartModelId)).unwrap();
            successNotify('Part model deleted successfully');
            setShowDeleteModal(false);
            setSelectedPartModelId(null);
            
            // Refresh table and overview
            dispatch(fetchPartModels({
                page: currentPage,
                limit: limit,
                body: {}
            }));
            dispatch(getPartModelsOverview());
        } catch (error) {
            errorNotify(error);
        }
    };

    useEffect(() => {
        const transformedFilters = {};
        Object.keys(searchFilters).forEach(key => {
            let value = searchFilters[key].value;
            let filterType = searchFilters[key].filter_type || 'contains';

            if (Array.isArray(value)) {
                filterType = 'one_of';
            } else if (filterType === 'one_of') {
                value = [value];
            }

            if (key === 'statusFilter' && !Array.isArray(value)) {
                filterType = 'equals';
            }

            transformedFilters[key] = {
                value: value,
                filter_type: filterType
            };
        });

        dispatch(fetchPartModels({
            page: currentPage,
            limit: limit,
            body: {
                ...transformedFilters,
                search_text: searchText
            }
        }));
    }, [currentPage, limit, searchText, searchFilters, dispatch]);

    const columns = [
        {
            name: "ID",
            key: "id",
            selector: (row) => row?.id,
            width: "80px",
            tableColumn: true,
            filterName: "idFilter",
            type: "number",
        },
        {
            name: "Name",
            key: "name",
            selector: (row) => row?.name,
            tableColumn: true,
            filterName: "nameFilter",
            type: "text",
        },
        {
            name: "Status",
            key: "status",
                   selector: (row) => (
                <Badge bg={row?.is_active == "true" ? 'success' : 'danger'} className="text-capitalize">
                    {row?.is_active == "true" ? 'Active' : 'Inactive'}
                </Badge>
            ),
            tableColumn: true,
            filterName: "statusFilter",
            type: "text",
            options: partModelStatusOptions
        },
        {
            name: "Created At",
            key: "created_at",
            selector: (row) => row?.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
            width: "150px",
        },
        {
            name: "Actions",
            key: "actions",
            width: "100px",
            selector: (row) => (
                <div className="table_action_btn">
                    <button onClick={() => handleEditPartModel(row)} className="edit_btn">
                        <LuPencil />
                    </button>
                    <button onClick={() => {
                        setSelectedPartModelId(row?.id);
                        setShowDeleteModal(true);
                    }} className="delete_btn">
                        <RiDeleteBinLine />
                    </button>
                </div>
            ),
        }
    ];

    return (
        <div className="container-fluid py-4">
            <Breadcrumbs values={['Maintenance', 'Part Models']} />

            <div className='page_header access_view mb-4'>
                <h2>Part Models</h2>
                <div className='d-flex align-items-center gap-2'>
                    <Button
                        submitText={<span>{plusIcon} Add Part Model</span>}
                        type="button"
                        shouldRow={false}
                        isShowSubmitBtn={true}
                        submitBtn={() => setShowCreateModal(true)}
                        isShowCancel={false}
                    />
                </div>
            </div>
            
            <PartModelOverviewCards />

            <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                    <NewTableComponent
                        pageName="Part Models"
                        columns={columns}
                        data={partModelsData?.results || []}
                        totalItems={partModelsData?.meta?.totalItems || 0}
                        totalPages={partModelsData?.meta?.totalPages || 0}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        limit={limit}
                        setLimit={setLimit}
                        loading={partModelsLoading}
                        onSearch={(text) => {
                            setSearchText(text);
                            setCurrentPage(1);
                        }}
                        searchFilters={searchFilters}
                        setSearchFilters={setSearchFilters}
                    />
                </div>
            </div>

            <CreatePartModelModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                currentPage={currentPage}
                limit={limit}
            />

            <UpdatePartModelModal
                show={showUpdateModal}
                onHide={() => {
                    setShowUpdateModal(false);
                    setSelectedPartModel(null);
                }}
                partModel={selectedPartModel}
                currentPage={currentPage}
                limit={limit}
            />

            <CustomModal show={showDeleteModal} handleClose={() => setShowDeleteModal(false)} size="md" heading="Delete Part Model" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to delete this part model?</p>

                    <Button
                        cancelBtn={() => setShowDeleteModal(false)}
                        submitBtn={handleDeletePartModel}
                        loading={deletePartModelLoading}
                        disabled={deletePartModelLoading}
                        buttonExtraCss="delete_user_modal_btn"
                        submitText="Delete"
                        type="submit"
                        isShowCancel={true}
                    />
                </div>
            </CustomModal>
        </div>
    );
};

export default PartModels;
